import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../../libs/shared/helpers/src/internationalization/react';
import {
  FrontEvent,
  Handler,
  HandlerOf,
} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {InlineLinkButton} from '../../../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {Dropdown, DropdownProps} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItemContent} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {SimpleDropdownItem} from '../../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {TagModel} from '../../../../../../models/api/tagModel';
import {DropdownBanner} from '../../../../../common/dropdowns/dropdownBanner';
import {DropdownSeparator} from '../../../../../common/dropdowns/dropdownSeparator';
import {PermissionDropdownItem} from '../../../../../common/dropdowns/permissionDropdownItem';
import {PermissionSimpleDropdownItem} from '../../../../../common/dropdowns/permissionSimpleDropdownItem';

/*
 * Props.
 */

interface TagDropdownProps extends DropdownProps {
  tag: TagModel;
  hasEditPermission: boolean;
  hasTagDescription: boolean;
  hasRemovePermission: boolean;
  hasVisibleCustomView: boolean;
  canPinTag: boolean;
  canMakeQuickRule: boolean;
  onNavigateToInboxClick: Handler;
  onViewConversationsClick: Handler;
  onCreateRuleClick: HandlerOf<FrontEvent>;
  onEditClick: HandlerOf<FrontEvent>;
  onUntagClick?: HandlerOf<FrontEvent>;
  onShowClick: Handler;
  onHideClick: Handler;
}

const StyledDescription = styled.div`
  display: flex;
  margin: 5px 12px 5px 12px;
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.tiny};
  color: ${(p) => p.theme.greys.shade70};

  justify-content: center;
  width: -webkit-fill-available;
`;

const StyledInlineLinkButton = styled(InlineLinkButton)`
  display: flex;
`;

const StyledIcon = styled(Icon)`
  margin-right: 5px;
`;

/*
 * Component.
 */

export const TagDropdown: FC<TagDropdownProps> = (props) => {
  const theme = useUserTheme();
  const {hasVisibleCustomView, hasTagDescription, tag, canPinTag, canMakeQuickRule} = props;
  const {isMainInboxTag, archivedAt} = tag;

  return (
    <Dropdown {...props} maxWidth={220}>
      <DropdownList {...props}>
        {archivedAt && (
          <DropdownBanner iconName="infoSmall">
            <Trans id="tag-details-archived">This tag is archived</Trans>
          </DropdownBanner>
        )}
        {!isMainInboxTag && hasTagDescription && (
          <>
            {tag.description ? (
              <StyledDescription>
                <Trans id="tag-details-description">{tag.description}</Trans>
              </StyledDescription>
            ) : (
              <StyledDescription>
                <StyledInlineLinkButton isDisabled={!props.hasEditPermission} onClick={props.onEditClick}>
                  <StyledIcon name="magicWand" color={theme.interactiveIcon} size={13} />
                  Add description
                </StyledInlineLinkButton>
              </StyledDescription>
            )}
            <DropdownSeparator />
          </>
        )}
        {isMainInboxTag && (
          <SimpleDropdownItem onSelect={props.onNavigateToInboxClick}>
            <Trans id="tag-details-gotoinbox">Go to Inbox</Trans>
          </SimpleDropdownItem>
        )}
        {!isMainInboxTag && (
          <SimpleDropdownItem onSelect={props.onViewConversationsClick}>
            <Trans id="tag-details-viewconversations">View conversations</Trans>
          </SimpleDropdownItem>
        )}
        {canPinTag && !isMainInboxTag && hasVisibleCustomView && !archivedAt && (
          <PermissionSimpleDropdownItem onSelect={props.onHideClick}>
            <Trans id="tag-details-hideinsidebar">Hide in sidebar</Trans>
          </PermissionSimpleDropdownItem>
        )}
        {canPinTag && !isMainInboxTag && !hasVisibleCustomView && !archivedAt && (
          <PermissionSimpleDropdownItem onSelect={props.onShowClick}>
            <Trans id="tag-details-showinsidebar">Show in sidebar</Trans>
          </PermissionSimpleDropdownItem>
        )}
        {canMakeQuickRule && !isMainInboxTag && !archivedAt && (
          <PermissionSimpleDropdownItem onSelect={props.onCreateRuleClick}>
            <Trans id="tag-details-createrule">Create quick rule</Trans>
          </PermissionSimpleDropdownItem>
        )}
        {!isMainInboxTag && <DropdownSeparator />}
        {!isMainInboxTag && (
          // TODO: Pass permission prop for access check instead of getHasEditPermission selector.
          <PermissionDropdownItem onSelect={props.onEditClick} isDisabled={!props.hasEditPermission}>
            <DropdownItemContent>
              <Trans id="tag-details-edit">Edit</Trans>
            </DropdownItemContent>
          </PermissionDropdownItem>
        )}
        {/* TODO: Pass permission prop for access check instead of getHasRemovePermission selector */}
        {props.onUntagClick && (
          <PermissionSimpleDropdownItem onSelect={props.onUntagClick} isDisabled={!props.hasRemovePermission}>
            <Trans id="tag-details-remove">Remove</Trans>
          </PermissionSimpleDropdownItem>
        )}
      </DropdownList>
    </Dropdown>
  );
};
